import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useStable } from "../context/StableContext";
import FeedingRationCard from "../components/FeedingRationCard";
import api, { errText, fileUrl } from "../lib/api";
import HorseImage from "../components/HorseImage";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { DateField } from "../components/DateField";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ArrowLeft, Pencil, Hammer, CalendarClock, Loader2, Paperclip, Trash2, RotateCcw, Plus, ChevronRight, Printer } from "lucide-react";
import OperationalStatusControl, { OperationalBadge, operationalHint } from "../components/OperationalStatus";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { CreateTherapy } from "./Therapies";
import { DeadlineFormDialog } from "./Deadlines";
import { FarrierFormDialog } from "./Farrier";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function HorseDetail() {
  const { current, perms } = useStable();
  const { id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useOutletContext();
  const [horse, setHorse] = useState(null);
  const [addDeadline, setAddDeadline] = useState(false);
  const [addFarrier, setAddFarrier] = useState(false);
  const [addTherapy, setAddTherapy] = useState(false);
  const [farrier, setFarrier] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [health, setHealth] = useState([]);
  const [therapies, setTherapies] = useState({ active: [], history: [] });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [arc, setArc] = useState({ reason: "uscito_trasferito", archive_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delHist, setDelHist] = useState(null);
  const [outOpen, setOutOpen] = useState(false);
  const [outForm, setOutForm] = useState({ out_date: new Date().toISOString().slice(0, 10), return_date: "", reason: "", notes: "" });
  const opRef = useRef(null);
  const openTemp = () => { setSheetOpen(false); setTimeout(() => opRef.current?.openTemp(), 60); };

  const load = useCallback(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/horses/${id}`)
      .then((r) => setHorse(r.data))
      .catch((err) => {
        const code = err.response?.status;
        toast.error(code === 403 ? "Non hai accesso a questo cavallo" : "Cavallo non trovato");
        navigate("/cavalli");
      });
    api.get(`/stables/${current.id}/farrier`, { params: { horse_id: id } }).then((r) => setFarrier(r.data)).catch(() => {});
    api.get(`/stables/${current.id}/deadlines`, { params: { horse_id: id } }).then((r) => setDeadlines(r.data)).catch(() => {});
    api.get(`/stables/${current.id}/health-history`, { params: { horse_id: id } }).then((r) => setHealth(r.data)).catch(() => {});
    api.get(`/stables/${current.id}/horses/${id}/therapies`).then((r) => setTherapies(r.data)).catch(() => {});
  }, [current, id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (horse) setPageTitle(horse.display_name); }, [horse, setPageTitle]);

  if (!horse) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>;

  const lastFarrier = farrier[0];
  const nextFarrier = farrier.find((f) => f.next_date && new Date(f.next_date) >= new Date()) || (lastFarrier?.next_date ? lastFarrier : null);

  const ag = ageYears(horse.dob);

  const info = [
    ["Microchip", horse.microchip], ["UELN", horse.ueln],
    ["Passaporto", horse.passport_number], ["Data di nascita", fmt(horse.dob)],
    ["Età", ag != null ? `${ag} anni` : null], ["Sesso", horse.sex],
    ["Razza", horse.breed], ["Mantello", horse.coat], ["Proprietario", horse.owner_name], ["Categoria", horse.category],
  ];

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.post(`/stables/${current.id}/horses/${id}/archive`, arc);
      toast.success("Cavallo archiviato");
      navigate("/archivio");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRestore = async () => {
    try {
      await api.post(`/stables/${current.id}/horses/${id}/restore`);
      toast.success("Cavallo ripristinato");
      load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const applyPresence = async (ps, extra, msg) => {
    try { await api.post(`/stables/${current.id}/horses/${id}/presence`, { presence_status: ps, ...(extra || {}) }); toast.success(msg); setSheetOpen(false); setOutOpen(false); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  const openOut = () => {
    const u = horse.presence_out || {};
    setOutForm({ out_date: u.start_date || new Date().toISOString().slice(0, 10), return_date: horse.presence_return_date || "", reason: u.reason || "", notes: u.notes || "" });
    setSheetOpen(false); setTimeout(() => setOutOpen(true), 60);
  };
  const submitOut = () => applyPresence("assente_temporaneo",
    { out_date: outForm.out_date, return_date: outForm.return_date || null, reason: outForm.reason || null, notes: outForm.notes || null },
    "Stato aggiornato · fuori scuderia");
  const applyOp = async (status, extra, msg) => {
    try { await api.post(`/stables/${current.id}/horses/${id}/operational-status`, { status, ...(extra || {}) }); toast.success(msg); setSheetOpen(false); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const deleteHistory = async () => {
    try {
      await api.delete(`/stables/${current.id}/health-history/${delHist.id}`);
      toast.success("Registrazione eliminata definitivamente");
      setDelHist(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  // Which operative/history cards actually have data (empty ones are never rendered).
  // Scadenze card = OPERATIONAL current records only. A completed deadline is a past performance
  // (it lives in "Storico sanitario"); its informational "next due" is the current record and must
  // not appear as a second operational row here. Mirrors the main Scadenzario operational semantics.
  const operationalDeadlines = deadlines.filter((d) => !d.completed && d.status !== "completato");
  const hasDeadlines = operationalDeadlines.length > 0;
  const hasFarrierCurrent = !!(lastFarrier || nextFarrier?.next_date);
  const hasActiveTherapies = therapies.active.length > 0;
  const hasOperative = hasDeadlines || hasFarrierCurrent || hasActiveTherapies;
  const hasHealthHist = health.length > 0;
  const hasFarrierHist = farrier.length > 0;
  const hasTherapyHist = therapies.history.length > 0;
  const hasStatusHist = (horse.unavailability_history || []).length > 0;
  const hasHistory = hasHealthHist || hasFarrierHist || hasTherapyHist || hasStatusHist;
  const opCount = [hasDeadlines, hasFarrierCurrent, hasActiveTherapies].filter(Boolean).length;
  const histCount = [hasHealthHist, hasFarrierHist, hasTherapyHist, hasStatusHist].filter(Boolean).length;
  const gridCls = (n) => n <= 1 ? "grid gap-4 grid-cols-1" : n === 2 ? "grid gap-4 sm:grid-cols-2" : "grid gap-4 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div className="space-y-6 eq-fade-up max-w-6xl mx-auto pb-24 lg:pb-8">
      {/* Back + primary actions */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/cavalli")} className="flex items-center gap-1 text-sm text-[#5F6B64]" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Cavalli
        </button>
      </div>

      {/* 1+2. ANAGRAFICA COMPLETA (foto + identità + dati) + STATO OPERATIVO */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="bg-white rounded-xl border border-[#E5DEC9] overflow-hidden lg:col-span-2" data-testid="horse-anagrafica">
          <div className="flex flex-col sm:flex-row">
            <div className="w-full h-40 sm:w-52 sm:h-auto sm:min-h-full bg-[#F0EAE1] shrink-0">
              <HorseImage photo={horse.photo} alt={horse.display_name} className="h-full w-full" fallbackClass="h-full w-full" />
            </div>
            <div className="p-4 sm:p-6 flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1B3B2B] truncate">{horse.display_name}</h1>
                  {horse.official_name && <p className="text-[#5F6B64] text-sm truncate">{horse.official_name}</p>}
                </div>
                {!horse.archived && perms.canWrite && (
                  <div className="flex items-center gap-1 shrink-0 -mt-1 -mr-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/stampe?horse=${id}`)} data-testid="print-cover-btn" className="text-[#8C532B] hover:bg-[#F0EAE1]">
                      <Printer className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Stampa copertina</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/cavalli/${id}/modifica`)} data-testid="edit-horse-btn" className="text-[#8C532B] hover:bg-[#F0EAE1]">
                      <Pencil className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Modifica</span>
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 mt-4">
                {info.map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <p className="font-mono-label text-[10px] text-[#8C532B]">{k}</p>
                    <p className="text-sm text-[#1E2421] font-medium break-words">{v || "—"}</p>
                  </div>
                ))}
              </div>
              {horse.notes && <p className="mt-4 text-sm text-[#5F6B64] bg-[#FCFAF6] rounded-lg p-3 border border-[#F0EAE1]">{horse.notes}</p>}
              <DestinazioneRow current={current} horseId={id} value={horse.destinazione} canManage={perms.canManage && !horse.archived} onChanged={load} />
            </div>
          </div>
        </div>

        {!horse.archived ? (
          <StatusCard horse={horse} canWrite={perms.canWrite} onManage={() => setSheetOpen(true)} />
        ) : (
          <Section title="Stato operativo" testid="horse-status-archived">
            <p className="text-sm text-[#8C532B] pt-1">In archivio · {horse.archive_reason}</p>
            {perms.canManage && <Button variant="outline" size="sm" onClick={doRestore} className="mt-3 text-[#1B5E20] border-[#1B5E20]"><RotateCcw className="h-4 w-4 mr-2" /> Ripristina</Button>}
          </Section>
        )}
      </div>

      {!horse.archived && (
        <FeedingRationCard stableId={current.id} horseId={id} onOpen={() => navigate(`/alimentazione?horse=${id}`)} />
      )}

      {/* 3. SITUAZIONE OPERATIVA */}
      {!horse.archived && (
        <div data-testid="horse-operative">
          <h2 className="font-mono-label text-[11px] text-[#8C532B] mb-2 px-1">Situazione operativa</h2>
          <div className={gridCls(3)}>
            {hasDeadlines ? (
              <HistCard title="Scadenze" testid="horse-deadlines" action={() => navigate(`/scadenze?horse=${id}`)} onAdd={perms.canOps ? () => setAddDeadline(true) : null} items={operationalDeadlines} render={(d) => (
                  <div key={d.id} className="flex items-center justify-between py-2 gap-2">
                    <div className="min-w-0"><p className="font-medium text-[#1B3B2B] truncate">{d.category}</p>
                      <p className="text-xs text-[#5F6B64]">Scadenza: {fmt(d.due_date)}</p></div>
                    <StatusBadge status={d.status} />
                  </div>
                )} />
            ) : <EmptyCard title="Scadenze" testid="horse-deadlines" label="Nessuna scadenza registrata" onAdd={perms.canOps ? () => setAddDeadline(true) : null} />}
            {hasFarrierCurrent ? (
              <Section title="Mascalcia" testid="horse-farrier-current" action={() => navigate(`/mascalcia?focus=${id}`)} onAdd={perms.canOps ? () => setAddFarrier(true) : null}>
                {lastFarrier && (
                  <div className="py-2">
                    <p className="font-mono-label text-[10px] text-[#8C532B]">Ultimo</p>
                    <p className="font-medium text-[#1B3B2B]">{lastFarrier.work_type}</p>
                    <p className="text-xs text-[#5F6B64]">{fmt(lastFarrier.date)}{lastFarrier.farrier ? ` · ${lastFarrier.farrier}` : ""}</p>
                  </div>
                )}
                {nextFarrier?.next_date && (
                  <div className="py-2">
                    <p className="font-mono-label text-[10px] text-[#8C532B]">Prossimo</p>
                    <p className="font-medium text-[#1B3B2B]">{fmt(nextFarrier.next_date)}</p>
                    <p className="text-xs text-[#5F6B64]">{nextFarrier.work_type}{nextFarrier.farrier ? ` · ${nextFarrier.farrier}` : ""}</p>
                  </div>
                )}
              </Section>
            ) : <EmptyCard title="Mascalcia" testid="horse-farrier-current" label="Nessun intervento registrato" onAdd={perms.canOps ? () => setAddFarrier(true) : null} />}
            {hasActiveTherapies ? (
              <Section title="Terapie" testid="horse-therapies" action={() => navigate(`/terapie`)} onAdd={perms.canOps ? () => setAddTherapy(true) : null}>
                {therapies.active.map((t) => (
                  <div key={t.id} className="py-2" data-testid={`horse-therapy-${t.id}`}>
                    <p className="font-medium text-[#1B3B2B]">{t.treatment_name} <span className="text-xs font-normal text-[#2E7D5B]">· Attiva</span></p>
                    <p className="text-xs text-[#5F6B64]">{t.start_date} → {t.end_date}{t.frequency_label ? ` · ${t.frequency_label}` : ""} · {t.admin_done}/{t.admin_total} eseguite</p>
                  </div>
                ))}
              </Section>
            ) : <EmptyCard title="Terapie" testid="horse-therapies" label="Nessuna terapia attiva" onAdd={perms.canOps ? () => setAddTherapy(true) : null} />}
          </div>
        </div>
      )}

      {/* 4. STORICO */}
      {hasHistory && (
        <div data-testid="horse-history">
          <h2 className="font-mono-label text-[11px] text-[#8C532B] mb-2 px-1">Storico</h2>
          <div className={gridCls(histCount)}>
            {hasHealthHist && (
              <HistCard title="Storico sanitario" testid="horse-health-history" items={health} render={(h) => (
                  <div key={h.id} className="flex items-start justify-between py-2 gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-[#1B3B2B]">{h.category}</p>
                      <p className="text-xs text-[#5F6B64]">Completato {fmt(h.completion_date)}{h.next_due_date ? ` · Prossima ${fmt(h.next_due_date)}` : ""}</p>
                      {h.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {h.attachments.map((a, i) => (
                            <a key={i} href={fileUrl(a.path)} target="_blank" rel="noreferrer" data-testid={`health-attachment-${h.id}-${i}`}
                              className="inline-flex items-center gap-1 text-xs rounded-full bg-[#F0EAE1] text-[#8C532B] px-2 py-0.5 hover:underline">
                              <Paperclip className="h-3 w-3" /> {a.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    {perms.canManage && (
                      <Button variant="ghost" size="icon" onClick={() => setDelHist(h)} data-testid={`delete-health-${h.id}`} title="Elimina dallo storico" className="shrink-0">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                )} />
            )}
            {hasFarrierHist && (
              <HistCard title="Storico mascalcia" testid="horse-farrier-history" items={farrier} render={(f) => (
                  <div key={f.id} className="flex items-center justify-between py-2 gap-2">
                    <div className="min-w-0"><p className="font-medium text-[#1B3B2B]">{f.work_type}</p>
                      <p className="text-xs text-[#5F6B64]">{fmt(f.date)}{f.farrier ? ` · ${f.farrier}` : ""}</p></div>
                    {f.cost != null && <span className="text-sm font-semibold text-[#8C532B] shrink-0">€ {Number(f.cost).toFixed(2)}</span>}
                  </div>
                )} />
            )}
            {hasTherapyHist && (
              <HistCard title="Storico terapie" testid="horse-therapy-history" items={therapies.history} render={(t) => (
                  <div key={t.id} className="py-2">
                    <p className="font-medium text-[#1B3B2B]">{t.treatment_name} <span className="text-xs font-normal" style={{ color: t.status === "stopped" ? "#B91C1C" : "#5F6B64" }}>· {t.status === "stopped" ? "Interrotta" : "Conclusa"}</span></p>
                    <p className="text-xs text-[#5F6B64]">{t.start_date} → {t.end_date} · {t.admin_done}/{t.admin_total} eseguite</p>
                  </div>
                )} />
            )}
            <StatusHistorySection horse={horse} canManage={perms.canManage} stableId={current.id} onChanged={load} testid="horse-status-history" />
          </div>
        </div>
      )}

      {/* Mobile quick actions */}
      {!horse.archived && (perms.canOps || perms.canWrite) && (
        <div className="lg:hidden fixed bottom-28 z-40" style={{ right: "calc(1rem + env(safe-area-inset-right))" }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="horse-quick-add" className="h-14 w-14 rounded-full bg-[#1B3B2B] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform">
                <Plus className="h-6 w-6" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 mb-2">
              {perms.canOps && <DropdownMenuItem data-testid="qa-add-deadline" onClick={() => setAddDeadline(true)}><CalendarClock className="h-4 w-4 mr-2" /> Aggiungi scadenza</DropdownMenuItem>}
              {perms.canOps && <DropdownMenuItem data-testid="qa-add-farrier" onClick={() => setAddFarrier(true)}><Hammer className="h-4 w-4 mr-2" /> Registra mascalcia</DropdownMenuItem>}
              {perms.canWrite && <DropdownMenuItem data-testid="qa-edit" onClick={() => navigate(`/cavalli/${id}/modifica`)}><Pencil className="h-4 w-4 mr-2" /> Modifica dati</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Quick-add inline forms (reuse the exact module components — no duplication, no navigation) */}
      {addDeadline && <DeadlineFormDialog initial={{ horse_ids: [id], due_auto: false }} stableId={current.id} horses={horse ? [horse] : []} onClose={() => setAddDeadline(false)} onSaved={load} />}
      {addFarrier && <FarrierFormDialog initial={{ horse_ids: [id] }} stableId={current.id} horses={horse ? [horse] : []} onClose={() => setAddFarrier(false)} onSaved={load} />}
      {addTherapy && <CreateTherapy stableId={current.id} horses={horse ? [horse] : []} preselectHorseId={id} onClose={() => setAddTherapy(false)} onSaved={(opts) => { if (!opts?.keepOpen) setAddTherapy(false); load(); }} />}

      {/* Archive dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Archivia cavallo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5F6B64]">Il cavallo lascia la gestione quotidiana e va in Archivio: non comparirà più tra i cavalli attivi, ma tutto il suo storico resterà consultabile.</p>
            <div>
              <Label>Motivo</Label>
              <Select value={arc.reason} onValueChange={(v) => setArc({ ...arc, reason: v })}>
                <SelectTrigger className="mt-1.5" data-testid="archive-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="uscito_trasferito">Trasferimento</SelectItem>
                  <SelectItem value="venduto">Vendita</SelectItem>
                  <SelectItem value="deceduto">Decesso</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data archiviazione</Label>
              <DateField className="mt-1.5" testId="archive-date" value={arc.archive_date} onChange={(v) => setArc({ ...arc, archive_date: v })} />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea className="mt-1.5" data-testid="archive-notes" value={arc.notes} onChange={(e) => setArc({ ...arc, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>Annulla</Button>
            <Button onClick={doArchive} disabled={busy} data-testid="confirm-archive-btn" className="bg-[#8C532B] hover:bg-[#734424]">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Archivia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Gestisci stato" action sheet — single entry point */}
      <Dialog open={sheetOpen} onOpenChange={(o) => { if (!o) { setSheetOpen(false); } }}>
        <DialogContent data-testid="horse-status-sheet" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display">Cosa sta succedendo a {horse.display_name}?</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {horse.presence_status === "presente" ? (
              <button type="button" data-testid="act-out" onClick={openOut} className="w-full text-left rounded-lg border border-[#E5DEC9] px-3 py-2.5 hover:bg-[#FCFAF6] transition-colors">
                <p className="font-semibold text-sm text-[#1B3B2B]">È temporaneamente fuori scuderia</p>
                <p className="text-xs text-[#5F6B64] mt-0.5">Resta in gestione, segnalato come assente. Potrai indicare data di uscita, rientro previsto, motivo/destinazione e note.</p></button>
            ) : (
              <>
                <button type="button" data-testid="act-in" onClick={() => applyPresence("presente", {}, "Segnato come rientrato in scuderia")} className="w-full text-left rounded-lg border border-[#E5DEC9] px-3 py-2.5 hover:bg-[#FCFAF6] transition-colors">
                  <p className="font-semibold text-sm text-[#1B3B2B]">È rientrato in scuderia</p>
                  <p className="text-xs text-[#5F6B64] mt-0.5">Torna presente in scuderia.</p></button>
                <button type="button" data-testid="act-out-edit" onClick={openOut} className="w-full text-left rounded-lg border border-[#E5DEC9] px-3 py-2.5 hover:bg-[#FCFAF6] transition-colors">
                  <p className="font-semibold text-sm text-[#1B3B2B]">Modifica uscita / rientro previsto</p>
                  <p className="text-xs text-[#5F6B64] mt-0.5">Aggiorna la data di rientro prevista, il motivo o le note dell'assenza.</p></button>
              </>
            )}
            <button type="button" data-testid="act-stop" onClick={openTemp} className="w-full text-left rounded-lg border border-[#E5DEC9] px-3 py-2.5 hover:bg-[#FCFAF6] transition-colors">
              <p className="font-semibold text-sm text-[#1B3B2B]">È temporaneamente fermo</p>
              <p className="text-xs text-[#5F6B64] mt-0.5">Indisponibile per riposo/infortunio: potrai indicare motivo, prognosi (data o intervallo di giorni), rivalutazione e note.</p></button>
            {horse.operational_status && horse.operational_status !== "attivo" && (
              <button type="button" data-testid="act-active" onClick={() => applyOp("attivo", {}, "Segnato come nuovamente attivo")} className="w-full text-left rounded-lg border border-[#E5DEC9] px-3 py-2.5 hover:bg-[#FCFAF6] transition-colors">
                <p className="font-semibold text-sm text-[#1B3B2B]">È nuovamente attivo</p>
                <p className="text-xs text-[#5F6B64] mt-0.5">Torna pienamente operativo.</p></button>
            )}
            <button type="button" data-testid="act-retire" onClick={() => applyOp("ritirato", {}, "Segnato come ritirato dall'attività")} className="w-full text-left rounded-lg border border-[#E5DEC9] px-3 py-2.5 hover:bg-[#FCFAF6] transition-colors">
              <p className="font-semibold text-sm text-[#1B3B2B]">È ritirato dall'attività</p>
              <p className="text-xs text-[#5F6B64] mt-0.5">Rimane in scuderia e in anagrafica, ma non più in attività.</p></button>
            {perms.canManage && (
              <div className="pt-2 border-t border-[#F0EAE1]">
                <button type="button" data-testid="act-leave" onClick={() => { setSheetOpen(false); setArchiveOpen(true); }} className="w-full text-left rounded-lg border border-[#E7D3B8] px-3 py-2.5 hover:bg-[#FBF3E7] transition-colors">
                  <p className="font-semibold text-sm text-[#8C532B]">Lascia la scuderia</p>
                  <p className="text-xs text-[#5F6B64] mt-0.5">Va in Archivio: non comparirà più nella gestione quotidiana, ma lo storico resterà consultabile.</p></button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Headless operational-status control: owns the full prognosis dialog */}
      {!horse.archived && (
        <OperationalStatusControl ref={opRef} horse={horse} stableId={current.id} canWrite={perms.canWrite} onChange={load} />
      )}

      {/* Temporaneamente fuori scuderia (presence) — optional exit/return details */}
      <Dialog open={outOpen} onOpenChange={(o) => { if (!o) setOutOpen(false); }}>
        <DialogContent data-testid="horse-out-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display">Temporaneamente fuori scuderia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#5F6B64]">Il cavallo resta in gestione ma segnalato come assente. Tutti i campi sono facoltativi. Se indichi un rientro previsto, il cavallo tornerà automaticamente <span className="font-semibold">Presente</span> in quella data.</p>
            <div><Label>Data uscita</Label>
              <DateField className="mt-1.5" testId="out-date" value={outForm.out_date} onChange={(v) => setOutForm({ ...outForm, out_date: v })} allowFuture /></div>
            <div><Label>Rientro previsto (opzionale)</Label>
              <DateField className="mt-1.5" testId="out-return-date" value={outForm.return_date} onChange={(v) => setOutForm({ ...outForm, return_date: v })} allowFuture />
              {outForm.return_date && <button type="button" data-testid="out-return-clear" onClick={() => setOutForm({ ...outForm, return_date: "" })} className="text-[11px] text-[#B91C1C] hover:underline mt-1">Rimuovi data di rientro</button>}</div>
            <div><Label>Motivo / destinazione (opzionale)</Label>
              <Input className="mt-1.5" data-testid="out-reason" placeholder="Es. gara, trasferta, ricovero…" value={outForm.reason} onChange={(e) => setOutForm({ ...outForm, reason: e.target.value })} /></div>
            <div><Label>Note (opzionale)</Label>
              <Textarea className="mt-1.5" data-testid="out-notes" value={outForm.notes} onChange={(e) => setOutForm({ ...outForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutOpen(false)}>Annulla</Button>
            <Button onClick={submitOut} data-testid="out-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete health-history confirm */}
      <Dialog open={!!delHist} onOpenChange={(o) => !o && setDelHist(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-red-700">Elimina dallo storico sanitario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#2D3832]">
              Stai eliminando definitivamente una registrazione dallo storico sanitario del cavallo.
              Questa operazione non elimina semplicemente una scadenza e non può essere annullata. Vuoi continuare?
            </p>
            {delHist && (
              <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-2 text-sm">
                <p className="font-semibold text-[#1B3B2B]">{delHist.category}</p>
                <p className="text-xs text-[#5F6B64]">Completato {fmt(delHist.completion_date)}</p>
                {delHist.attachments?.length > 0 && <p className="text-xs text-[#8C532B] mt-1">Verranno eliminati anche {delHist.attachments.length} allegato/i.</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelHist(null)}>Annulla</Button>
            <Button onClick={deleteHistory} data-testid="confirm-delete-history-btn" className="bg-red-600 hover:bg-red-700 text-white">Elimina definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children, testid, action, onAdd, className }) {
  return (
    <div className={`bg-white rounded-xl border border-[#E5DEC9] p-5 ${className || ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <h3 className="font-display font-semibold text-[#1B3B2B]">{title}</h3>
        <div className="flex items-center gap-2 shrink-0">
          {onAdd && (
            <button onClick={onAdd} className="text-xs text-[#1B3B2B] hover:underline inline-flex items-center gap-0.5" data-testid={`${testid}-add`}>
              <Plus className="h-3.5 w-3.5" /> Aggiungi
            </button>
          )}
          {action && (
            <button onClick={action} className="text-xs text-[#8C532B] hover:underline inline-flex items-center gap-0.5" data-testid={`${testid}-more`}>
              Vedi <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="divide-y divide-[#F0EAE1]">{children}</div>
    </div>
  );
}

function EmptyCard({ title, testid, label, onAdd }) {
  return (
    <Section title={title} testid={testid} onAdd={onAdd}>
      <div className="py-4 text-center">
        <p className="text-sm text-[#9AA39C]">{label}</p>
        {onAdd && (
          <button onClick={onAdd} data-testid={`${testid}-empty-add`} className="mt-2 inline-flex items-center gap-1 text-sm text-[#1B3B2B] font-medium hover:underline">
            <Plus className="h-4 w-4" /> Aggiungi
          </button>
        )}
      </div>
    </Section>
  );
}

const DEST_LABELS = { dpa: "DPA", ndpa: "NDPA", non_specificato: "Non specificato" };
function DestinazioneRow({ current, horseId, value, canManage, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cur = value || "non_specificato";
  const change = async (v) => {
    if (v === cur) return;
    setBusy(true);
    try { await api.post(`/stables/${current.id}/horses/${horseId}/destinazione`, { destinazione: v }); toast.success("Destinazione aggiornata"); onChanged && onChanged(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="horse-destinazione">
      <span className="font-mono-label text-[10px] text-[#8C532B]">Destinazione</span>
      <span className="text-sm font-semibold text-[#1E2421]">{DEST_LABELS[cur]}</span>
      {canManage && (
        <Select value={cur} onValueChange={change} disabled={busy}>
          <SelectTrigger className="h-8 w-auto text-xs" data-testid="horse-destinazione-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="non_specificato">Non specificato</SelectItem>
            <SelectItem value="dpa">DPA</SelectItem>
            <SelectItem value="ndpa">NDPA</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function HistCard({ title, items, testid, render, action, onAdd }) {
  const [exp, setExp] = useState(false);
  if (!items || items.length === 0) return null;
  const shown = exp ? items : items.slice(0, 5);
  return (
    <Section title={title} testid={testid} action={action} onAdd={onAdd}>
      {shown.map(render)}
      {items.length > 5 && (
        <div className="pt-2">
          <button onClick={() => setExp(!exp)} data-testid={`${testid}-toggle`} className="text-xs text-[#8C532B] hover:underline">
            {exp ? "Riduci" : `Vedi tutto (${items.length})`}
          </button>
        </div>
      )}
    </Section>
  );
}

function ageYears(dob) {
  if (!dob) return null;
  const b = new Date(String(dob).slice(0, 10) + "T00:00:00");
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a >= 0 ? a : null;
}

// Presenza + Stato operativo (kept as distinct data dimensions, shown together for UX).
function StatusCard({ horse, canWrite, onManage }) {
  const cur = horse.operational_status || "attivo";
  const hint = operationalHint(horse);
  return (
    <div className="bg-white rounded-xl border border-[#E5DEC9] p-5" data-testid="horse-status-block">
      <h3 className="font-display font-semibold text-[#1B3B2B] mb-3">Stato operativo</h3>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={horse.presence_status} />
        <OperationalBadge status={cur} />
      </div>
      {horse.presence_status === "assente_temporaneo" && (
        <p className="text-[11px] text-[#5F6B64] mt-2 leading-snug" data-testid="presence-out-detail">
          Fuori scuderia{horse.presence_out?.reason ? ` · ${horse.presence_out.reason}` : ""}{horse.presence_out?.start_date ? ` · dal ${fmt(horse.presence_out.start_date)}` : ""}
          <br />{horse.presence_return_date ? `Rientro previsto: ${fmt(horse.presence_return_date)}` : "Nessuna data di rientro prevista"}
        </p>
      )}
      {cur === "temp_indisponibile" && hint && (
        <p className="text-[11px] text-[#8A5A00] mt-2 leading-snug" data-testid="operational-detail">{hint}</p>
      )}
      {canWrite && (
        <Button size="sm" variant="outline" data-testid="horse-status-sheet-btn" onClick={onManage} className="text-[#1B3B2B] border-[#1B3B2B] w-full mt-3">Gestisci stato</Button>
      )}
    </div>
  );
}

function StatusHistorySection({ horse, canManage, stableId, onChanged, testid }) {
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState(false);
  const hist = horse.unavailability_history || [];
  if (hist.length === 0) return null;
  const fmtD = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const rows = hist.map((e, idx) => ({ e, key: e.id || `idx:${idx}` })).reverse();
  const shown = exp ? rows : rows.slice(0, 5);
  const toggle = (k) => { const n = new Set(sel); n.has(k) ? n.delete(k) : n.add(k); setSel(n); };
  const doDelete = async () => {
    setBusy(true);
    try {
      const body = confirm === "all" ? { all: true } : { keys: Array.from(sel) };
      const r = await api.post(`/stables/${stableId}/horses/${horse.id}/status-history/delete`, body);
      toast.success(`Storico aggiornato · ${r.data.removed} eliminati`);
      setConfirm(null); setSel(new Set()); setSelMode(false); onChanged && onChanged();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Section title="Storico stati e disponibilità" testid={testid}>
      {canManage && (
        <div className="flex items-center justify-between gap-2 pb-2 flex-wrap">
          {!selMode ? (
            <Button variant="ghost" size="sm" data-testid="status-hist-select" onClick={() => setSelMode(true)}>Seleziona</Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => { setSelMode(false); setSel(new Set()); }}>Fine</Button>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" disabled={sel.size === 0} data-testid="status-hist-del-selected"
                  onClick={() => setConfirm("selected")} className="text-red-600 border-red-300 disabled:opacity-40">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina selezionati ({sel.size})
                </Button>
                <Button variant="outline" size="sm" data-testid="status-hist-del-all" onClick={() => setConfirm("all")} className="text-red-600 border-red-300">Elimina tutto</Button>
              </div>
            </>
          )}
        </div>
      )}
      {shown.map(({ e, key }) => (
        <div key={key} className="py-2 flex items-start gap-2.5" data-testid={`status-history-${key}`}>
          {selMode && (
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#8C532B] shrink-0" checked={sel.has(key)} onChange={() => toggle(key)} data-testid={`status-hist-check-${key}`} />
          )}
          <div className="min-w-0">
            <p className="text-sm text-[#1B3B2B]">{e.kind === "presence" ? "Fuori scuderia" : "Temporaneamente indisponibile"}</p>
            <p className="text-xs text-[#5F6B64] break-words">{fmtD(e.start_date)} → {fmtD(e.end_date)}{e.reason ? ` · ${e.reason}` : ""}{e.notes ? ` · ${e.notes}` : ""}</p>
          </div>
        </div>
      ))}
      {rows.length > 5 && (
        <div className="pt-2">
          <button onClick={() => setExp(!exp)} data-testid="status-hist-toggle" className="text-xs text-[#8C532B] hover:underline">
            {exp ? "Riduci" : `Vedi tutto (${rows.length})`}
          </button>
        </div>
      )}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="status-hist-confirm">
          <DialogHeader><DialogTitle className="font-display text-red-700">Elimina dallo storico stati</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            {confirm === "all"
              ? "Stai eliminando TUTTO lo storico stati e disponibilità di questo cavallo. L'operazione riguarda solo questo storico e non modifica lo stato o la presenza attuali, il sanitario, le terapie, la mascalcia, le scadenze o le presenze. Non può essere annullata."
              : `Stai eliminando ${sel.size} voce/i dallo storico stati e disponibilità. L'operazione riguarda solo questo storico e non modifica lo stato o la presenza attuali del cavallo. Non può essere annullata.`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Annulla</Button>
            <Button onClick={doDelete} disabled={busy} data-testid="status-hist-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
